import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { appointmentsAPI, notificationsAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Search,
  Calendar,
  Video,
  Home,
  FileText,
  Bell,
  MapPin,
  Clock,
  ArrowRight,
  Stethoscope,
  Heart,
  Activity,
  Baby,
  Sun,
  Eye,
  Smile,
  Move,
  Thermometer,
  Brain,
  Apple,
  ChevronRight,
  Sparkles,
  MessageCircle,
  Users
} from 'lucide-react';

const specialtyIcons = {
  general: Stethoscope,
  pediatric: Baby,
  gynecology: Heart,
  cardiology: Activity,
  dermatology: Sun,
  ophthalmology: Eye,
  dentistry: Smile,
  physiotherapy: Move,
  nursing: Thermometer,
  psychology: Brain,
  nutrition: Apple,
};

const specialtyColors = {
  general: 'bg-[#E53935]',
  pediatric: 'bg-[#E91E63]',
  gynecology: 'bg-[#9C27B0]',
  cardiology: 'bg-[#673AB7]',
  dermatology: 'bg-[#FF9800]',
  ophthalmology: 'bg-[#2196F3]',
  dentistry: 'bg-[#00BCD4]',
  physiotherapy: 'bg-[#4CAF50]',
  nursing: 'bg-[#8BC34A]',
  psychology: 'bg-[#3F51B5]',
  nutrition: 'bg-[#CDDC39]',
};

const PatientDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [appointmentsRes, notificationsRes] = await Promise.all([
        appointmentsAPI.getAll({ status: 'pending' }),
        notificationsAPI.getAll(),
      ]);
      setAppointments(appointmentsRes.data.appointments.slice(0, 3));
      setNotifications(notificationsRes.data.notifications.filter(n => !n.is_read).slice(0, 5));
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const quickActions = [
    {
      icon: Search,
      label: 'Trouver un médecin',
      description: 'Pres de chez vous',
      path: '/search',
      gradient: 'from-[#E53935] to-[#D32F2F]',
    },
    {
      icon: Calendar,
      label: 'Prendre RDV',
      description: 'Consultation sur place',
      path: '/search?type=consultation',
      gradient: 'from-[#2196F3] to-[#1E88E5]',
    },
    {
      icon: Video,
      label: 'Teleconsultation',
      description: 'Appel video rapide',
      path: '/search?type=video_call',
      gradient: 'from-[#1A237E] to-[#3949AB]',
    },
    {
      icon: Home,
      label: 'Soins a domicile',
      description: 'Un pro chez vous',
      path: '/home-care',
      gradient: 'from-[#FF9800] to-[#F57C00]',
    },
  ];

  const specialties = [
    { id: 'general', name: 'Généraliste' },
    { id: 'pediatric', name: 'Pédiatrie' },
    { id: 'gynecology', name: 'Gynécologie' },
    { id: 'cardiology', name: 'Cardiologie' },
    { id: 'dermatology', name: 'Dermatologie' },
    { id: 'ophthalmology', name: 'Ophtalmologie' },
    { id: 'dentistry', name: 'Dentiste' },
    { id: 'nursing', name: 'Infirmier' },
  ];

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-100 text-amber-700',
      confirmed: 'bg-[#E3F2FD] text-[#1A237E]',
      in_progress: 'bg-purple-100 text-purple-700',
      completed: 'bg-emerald-100 text-emerald-700',
      cancelled: 'bg-red-100 text-red-700',
    };
    const labels = {
      pending: 'En attente',
      confirmed: 'Confirme',
      in_progress: 'En cours',
      completed: 'Termine',
      cancelled: 'Annule',
    };
    return (
      <Badge className={`${styles[status] || 'bg-slate-100 text-slate-600'} rounded-lg text-xs`}>
        {labels[status] || status}
      </Badge>
    );
  };

  return (
    <div className="px-4 md:px-6 lg:px-8 py-6" data-testid="patient-dashboard">
      {/* Welcome Section */}
      <div className="mb-8">
        <div className="flex items-center gap-2 text-[#64748B] text-sm mb-1">
          <Sparkles className="w-4 h-4 text-[#E53935]" />
          <span>Bienvenue sur Dockhta</span>
        </div>
        <h1 
          className="text-2xl md:text-3xl font-bold text-[#1A237E]"
          style={{ fontFamily: 'Outfit, sans-serif' }}
        >
          Bonjour, {user?.full_name?.split(' ')[0]}!
        </h1>
        <p className="text-[#64748B] mt-1">Comment pouvons-nous vous aider aujourd&apos;hui?</p>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {quickActions.map((action, index) => {
          const Icon = action.icon;
          return (
            <Link
              key={index}
              to={action.path}
              className="group"
              data-testid={`quick-action-${index}`}
            >
              <Card className="card-interactive h-full border-0 shadow-sm hover:shadow-lg">
                <CardContent className="p-5">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${action.gradient} flex items-center justify-center mb-4 shadow-lg group-hover:scale-110 transition-transform`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-semibold text-[#1A237E] text-sm md:text-base" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {action.label}
                  </h3>
                  <p className="text-xs text-[#64748B] mt-1">{action.description}</p>
                </CardContent>
              </Card>
            </Link>
          );
        })}
      </div>

      {/* Main Content Grid */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left Column - Appointments & Specialties */}
        <div className="lg:col-span-2 space-y-6">
          {/* Upcoming Appointments */}
          <Card className="card-premium">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-5">
                <h2 
                  className="text-lg font-semibold text-[#1A237E]"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  Prochains rendez-vous
                </h2>
                <Link 
                  to="/appointments" 
                  className="text-sm text-[#2196F3] hover:text-[#1E88E5] font-medium flex items-center gap-1"
                >
                  Voir tout
                  <ChevronRight className="w-4 h-4" />
                </Link>
              </div>

              {loading ? (
                <div className="space-y-3">
                  {[1, 2].map((i) => (
                    <div key={i} className="animate-pulse flex gap-4 p-4 bg-slate-50 rounded-xl">
                      <div className="w-12 h-12 bg-slate-200 rounded-xl" />
                      <div className="flex-1 space-y-2">
                        <div className="h-4 bg-slate-200 rounded w-3/4" />
                        <div className="h-3 bg-slate-200 rounded w-1/2" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : appointments.length > 0 ? (
                <div className="space-y-3">
                  {appointments.map((appointment) => (
                    <div
                      key={appointment.id}
                      className="flex items-center gap-4 p-4 bg-[#F8FAFC] hover:bg-slate-100 rounded-xl cursor-pointer transition-colors"
                      onClick={() => navigate(`/appointments`)}
                      data-testid={`appointment-${appointment.id}`}
                    >
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#E53935] to-[#D32F2F] flex items-center justify-center text-white font-bold shadow">
                        {appointment.professional_name?.charAt(0) || 'D'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-[#1A237E] truncate">
                          Dr. {appointment.professional_name}
                        </h4>
                        <p className="text-sm text-[#64748B]">{appointment.professional_specialty}</p>
                        <div className="flex items-center gap-3 mt-1 text-xs text-[#64748B]">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {appointment.date}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {appointment.time_slot}
                          </span>
                        </div>
                      </div>
                      {getStatusBadge(appointment.status)}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-[#64748B]">Aucun rendez-vous a venir</p>
                  <Button
                    onClick={() => navigate('/search')}
                    className="mt-4 bg-[#E53935] hover:bg-[#D32F2F] text-white rounded-xl"
                    data-testid="book-appointment-btn"
                  >
                    Prendre rendez-vous
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Specialties Grid */}
          <Card className="card-premium">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-5">
                <h2 
                  className="text-lg font-semibold text-[#1A237E]"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  Explorer par spécialité
                </h2>
                <Link 
                  to="/search" 
                  className="text-sm text-[#2196F3] hover:text-[#1E88E5] font-medium flex items-center gap-1"
                >
                  Voir tout
                  <ChevronRight className="w-4 h-4" />
                </Link>
              </div>

              <div className="grid grid-cols-4 gap-3">
                {specialties.map((specialty) => {
                  const Icon = specialtyIcons[specialty.id] || Stethoscope;
                  const color = specialtyColors[specialty.id] || 'bg-[#E53935]';
                  return (
                    <Link
                      key={specialty.id}
                      to={`/search?specialty=${encodeURIComponent(specialty.name)}`}
                      className="specialty-card group"
                      data-testid={`specialty-${specialty.id}`}
                    >
                      <div className={`specialty-icon ${color} group-hover:scale-110 transition-transform shadow-lg`}>
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <span className="specialty-name">{specialty.name}</span>
                    </Link>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Notifications & Quick Links */}
        <div className="space-y-6">
          {/* Notifications */}
          <Card className="card-premium">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-5">
                <h2 
                  className="text-lg font-semibold text-[#1A237E]"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  Notifications
                </h2>
                <Link 
                  to="/notifications" 
                  className="text-sm text-[#2196F3] hover:text-[#1E88E5] font-medium"
                >
                  Voir tout
                </Link>
              </div>

              {notifications.length > 0 ? (
                <div className="space-y-3">
                  {notifications.slice(0, 4).map((notif) => (
                    <div
                      key={notif.id}
                      className="flex items-start gap-3 p-3 bg-[#F8FAFC] rounded-xl"
                    >
                      <div className="w-9 h-9 rounded-lg bg-[#E3F2FD] flex items-center justify-center flex-shrink-0">
                        <Bell className="w-4 h-4 text-[#2196F3]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[#1A237E] truncate">{notif.title}</p>
                        <p className="text-xs text-[#64748B] line-clamp-1">{notif.message}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6">
                  <Bell className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                  <p className="text-sm text-[#64748B]">Aucune notification</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Links */}
          <Card className="card-premium">
            <CardContent className="p-6">
              <h2 
                className="text-lg font-semibold text-[#1A237E] mb-5"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                Acces rapide
              </h2>
              <div className="space-y-2">
                <Link
                  to="/ai-assistant"
                  className="flex items-center gap-3 p-3 rounded-xl hover:bg-[#F8FAFC] transition-colors group"
                  data-testid="quick-link-ai"
                >
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#1976D2] to-[#7C3AED] flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Brain className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <span className="font-medium text-[#1A237E]">Assistant IA Sante</span>
                    <p className="text-xs text-[#64748B]">Posez vos questions sante</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-[#64748B]" />
                </Link>
                <Link
                  to="/chat"
                  className="flex items-center gap-3 p-3 rounded-xl hover:bg-[#F8FAFC] transition-colors group"
                  data-testid="quick-link-chat"
                >
                  <div className="w-10 h-10 rounded-xl bg-[#E53935]/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <MessageCircle className="w-5 h-5 text-[#E53935]" />
                  </div>
                  <div className="flex-1">
                    <span className="font-medium text-[#1A237E]">Messages</span>
                    <p className="text-xs text-[#64748B]">Discutez avec vos médecins</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-[#64748B]" />
                </Link>
                <Link
                  to="/medical-records"
                  className="flex items-center gap-3 p-3 rounded-xl hover:bg-[#F8FAFC] transition-colors group"
                  data-testid="quick-link-records"
                >
                  <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <FileText className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div className="flex-1">
                    <span className="font-medium text-[#1A237E]">Dossier medical</span>
                    <p className="text-xs text-[#64748B]">Consultez vos documents</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-[#64748B]" />
                </Link>
                <Link
                  to="/profile"
                  className="flex items-center gap-3 p-3 rounded-xl hover:bg-[#F8FAFC] transition-colors group"
                  data-testid="quick-link-profile"
                >
                  <div className="w-10 h-10 rounded-xl bg-[#E3F2FD] flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Heart className="w-5 h-5 text-[#2196F3]" />
                  </div>
                  <div className="flex-1">
                    <span className="font-medium text-[#1A237E]">Mon profil</span>
                    <p className="text-xs text-[#64748B]">Gerez vos informations</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-[#64748B]" />
                </Link>
                <Link
                  to="/patient/family"
                  className="flex items-center gap-3 p-3 rounded-xl hover:bg-[#F8FAFC] transition-colors group"
                  data-testid="quick-link-family"
                >
                  <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Users className="w-5 h-5 text-purple-600" />
                  </div>
                  <div className="flex-1">
                    <span className="font-medium text-[#1A237E]">Ma famille</span>
                    <p className="text-xs text-[#64748B]">Ajoutez des membres</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-[#64748B]" />
                </Link>
              </div>
            </CardContent>
          </Card>

          {/* Emergency CTA */}
          <Card className="bg-gradient-to-br from-[#E53935] to-[#D32F2F] border-0 shadow-lg">
            <CardContent className="p-6 text-center text-white">
              <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-4">
                <Stethoscope className="w-7 h-7" />
              </div>
              <h3 className="font-semibold text-lg mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Besoin d&apos;aide urgente?
              </h3>
              <p className="text-sm text-white/80 mb-4">
                Consultez un médecin maintenant par vidéo
              </p>
              <Button
                onClick={() => navigate('/search?type=video_call')}
                className="w-full bg-white text-[#E53935] hover:bg-white/90 rounded-xl font-semibold"
                data-testid="emergency-video-btn"
              >
                Teleconsultation urgente
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default PatientDashboard;
